import parse from "html-react-parser";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { IndicesFinanzero } from "../../../types/IndiceFinanzero";
import styles from "./styles.module.scss";

interface ComponentTypes {
  itemsFaq: Array<ItemsProps>;
  title?: string | JSX.Element;
  isIFE?: boolean;
  hideItemTypeFAQPage?: boolean;
  itemFaqOpened?: number;
  scrollToTitle?: boolean;
}
interface ItemsProps extends IndicesFinanzero {
  title: string;
  content: string | JSX.Element;
  uri?: string;
}

interface Children {
  children: React.ReactNode;
}

export const FaqBlock = ({
  itemsFaq,
  title = "Perguntas frequentes sobre a FinanZero",
  isIFE = false,
  hideItemTypeFAQPage = false,
  itemFaqOpened,
  scrollToTitle,
}: ComponentTypes) => {
  const [openItem, setOpenItem] = useState<number | null>(itemFaqOpened);
  const router = useRouter();
  const itemToScroll = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    itemToScroll.current?.scrollIntoView({ behavior: "smooth" });
  }, [itemToScroll, openItem]);

  useEffect(() => {
    if (itemFaqOpened === null && scrollToTitle) {
      setOpenItem(null);
      titleRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [itemFaqOpened, scrollToTitle, title]);

  const classObj = {
    header: [styles.questionHeader, styles.IFEquestionHeader].join(" "),
    title: {
      show: [
        styles.questionTitle,
        styles.IFEquestionTitleShow,
        styles.IFEquestionTitle,
      ].join(" "),
      hide: [
        styles.questionTitle,
        styles.IFEquestionTitleHide,
        styles.IFEquestionTitle,
      ].join(" "),
    },
    toggle: {
      show: [
        styles.questionHeaderToggle,
        styles.IFEquestionHeaderToggleShow,
      ].join(" "),
      hide: [
        styles.questionHeaderToggle,
        styles.IFEquestionHeaderToggleHide,
      ].join(" "),
    },
    content: [styles.questionContent, styles.IFEquestionContent].join(" "),
  };

  const DivWithItemType = ({ children }: Children) => {
    if (hideItemTypeFAQPage) {
      return <div>{children}</div>;
    } else {
      return (
        <div
          className="faq-questions"
          itemScope
          itemProp="mainEntity"
          itemType="https://schema.org/FAQPage"
        >
          {children}
        </div>
      );
    }
  };

  return (
    <>
      {typeof title === "string" ? (
        <h2 className={styles.title}>{title}</h2>
      ) : (
        title
      )}
      <DivWithItemType>
        {itemsFaq.length &&
          itemsFaq.map((item, index) => {
            return (
              <div
                key={index}
                className={!isIFE ? styles.question : null}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                data-open={index === openItem}
                ref={index === openItem ? itemToScroll : null}
              >
                <header
                  onClick={() => {
                    item.uri && router.push(item["uri"], "", { shallow: true });
                    openItem === index ? setOpenItem(null) : setOpenItem(index);
                  }}
                  className={!isIFE ? styles.questionHeader : classObj.header}
                >
                  <h3
                    className={
                      index == openItem
                        ? isIFE
                          ? classObj.title.show
                          : styles.questionTitle
                        : isIFE
                        ? classObj.title.hide
                        : styles.questionTitle
                    }
                    itemProp="name"
                  >
                    {item.title}
                  </h3>

                  <span
                    className={
                      index == openItem
                        ? isIFE
                          ? classObj.toggle.show
                          : styles.questionHeaderToggle
                        : isIFE
                        ? classObj.toggle.hide
                        : styles.questionHeaderToggle
                    }
                  ></span>
                </header>

                <div
                  className={!isIFE ? styles.questionContent : classObj.content}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div itemProp="text">
                    {typeof item.content === "string"
                      ? parse(item.content)
                      : item.content}
                  </div>
                </div>
              </div>
            );
          })}
      </DivWithItemType>
    </>
  );
};
